import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Box, Button, ButtonBase, Chip, Paper, Tooltip, Typography, Zoom } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useLocation, useNavigate } from 'react-router-dom';
import { getPlatformName } from '../../config/environment';
import { useAuth } from '../../contexts/AuthContext';
import {
  ASSISTANT_ASK_EVENT,
  AssistantSessionContext,
  AssistantStatus,
  getAssistantStatus,
  getSupportSeenSeq,
  HumanSupportThread,
  listHumanSupportThreads,
  SUPPORT_CENTER_DISPLAY_NAME,
  SUPPORT_SEEN_EVENT,
} from '../../services/assistantCentral';
import { ASSISTANT_ERROR_EVENT, AssistantRecentError } from '../../utils/assistantErrorBuffer';
import { useDeviceLayout } from '../../hooks/useDeviceLayout';
import {
  mobileEdgeTabFixedSx,
  mobileEdgeTabLabelSx,
  mobileEdgeTabBottom,
} from '../layout/floatingDockMobileEdge';
import {
  DOCK_CSS,
} from '../layout/floatingDockLayout';
import {
  assistantFabSx,
  assistantMobileEdgeTabSx,
  assistantPanelPaperSx,
  assistantPillDotSx,
  assistantWidgetPanelSizeSx,
} from './assistantTheme';
import { ASSISTANT_WIDGET_STORAGE_KEY, useAssistantChat } from './assistant/useAssistantChat';
import SupportMessenger, { useMessengerNav } from './messenger/SupportMessenger';

export function assistantGreeting(session?: AssistantSessionContext | null): string {
  const platform = getPlatformName();
  const first = session?.user_name?.trim().split(/\s+/)[0];
  const hello = first ? `Olá, ${first}!` : 'Olá!';
  const unitHint =
    session?.unit_selected && session.unit_name
      ? ` Estou considerando a unidade «${session.unit_name}» (a mesma do seletor no topo da tela).`
      : session && !session.unit_selected
        ? ' Selecione a unidade no topo da tela para eu verificar dados da clínica com precisão.'
        : '';
  return `${hello} Sou a ${SUPPORT_CENTER_DISPLAY_NAME} do ${platform}.${unitHint} Posso tirar dúvidas de qualquer tela e, quando liberado, verificar a sua conta (caixa, cobranças, sessões, agenda, WhatsApp, NFSe) para encontrar a causa do problema.`;
}

export function AssistantSessionContextNotice({ session }: { session?: AssistantSessionContext | null }) {
  if (!session) return null;
  if (!session.unit_selected) {
    return (
      <Alert severity="warning" sx={{ mb: 1, fontSize: '0.72rem', position: 'relative', zIndex: 1 }}>
        Nenhuma unidade ativa no seletor do topo — escolha a unidade para eu alinhar consultas e diagnósticos.
      </Alert>
    );
  }
  const prof = session.professional_name ? ` · ${session.professional_name}` : '';
  return (
    <Typography component="div" variant="caption" color="text.secondary" sx={{ mb: 0.75, px: 0.5, lineHeight: 1.35 }}>
      {session.role_label}
      {session.is_unit_manager ? ' (gestor)' : ''}
      {prof} · Unidade: {session.unit_name}
    </Typography>
  );
}

export function AssistantStatusNotice({
  status,
  onOpenSettings,
}: {
  status: AssistantStatus | null;
  onOpenSettings?: () => void;
}) {
  if (!status || status.tools_enabled) return null;
  return (
    <Alert
      severity="info"
      sx={{ mb: 1, fontSize: '0.72rem', position: 'relative', zIndex: 1 }}
      action={
        status.can_manage_settings && onOpenSettings ? (
          <Button color="inherit" size="small" onClick={onOpenSettings} sx={{ fontSize: '0.68rem', whiteSpace: 'nowrap' }}>
            Ativar consultas
          </Button>
        ) : undefined
      }
    >
      {status.can_manage_settings
        ? 'As consultas aos dados da clínica estão desligadas. Ative em Configurações para eu verificar caixa, sessões e agenda.'
        : `Nesta clínica eu respondo com orientações. Para eu verificar dados da conta, o Administrador Master precisa ativar as consultas na ${SUPPORT_CENTER_DISPLAY_NAME}.`}
    </Alert>
  );
}

const ERROR_PROMPT_MS = 12000;
const SUPPORT_THREADS_ACTIVE_MS = 45_000;
const SUPPORT_THREADS_IDLE_MS = 5 * 60_000;

const VirtualAssistantWidget: React.FC = () => {
  const { user } = useAuth();
  const { isMobileMd: mobileEdgeDock } = useDeviceLayout();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<AssistantStatus | null>(null);
  const [lastError, setLastError] = useState<AssistantRecentError | null>(null);
  const errorTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const chat = useAssistantChat({ storageKey: ASSISTANT_WIDGET_STORAGE_KEY, active: open });
  const chatRef = useRef(chat);
  chatRef.current = chat;
  const nav = useMessengerNav();
  const openChatRef = useRef(nav.openChat);
  openChatRef.current = nav.openChat;
  const [supportThreads, setSupportThreads] = useState<HumanSupportThread[]>([]);
  const [seenTick, setSeenTick] = useState(0);

  /** Respostas da equipe com o painel fechado: aviso no botão flutuante. */
  useEffect(() => {
    if (!user || open || (status && !status.enabled)) return undefined;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const load = async () => {
      let next = SUPPORT_THREADS_IDLE_MS;
      try {
        const threads = await listHumanSupportThreads();
        if (!alive) return;
        setSupportThreads(threads);
        if (threads.some((t) => t.status !== 'resolved')) next = SUPPORT_THREADS_ACTIVE_MS;
      } catch {
        /* tenta no próximo ciclo */
      }
      if (alive) timer = setTimeout(load, next);
    };
    void load();
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [user, open, status]);

  useEffect(() => {
    const onSeen = () => setSeenTick((n) => n + 1);
    window.addEventListener(SUPPORT_SEEN_EVENT, onSeen);
    return () => window.removeEventListener(SUPPORT_SEEN_EVENT, onSeen);
  }, []);

  const unreadSupportThreads = useMemo(
    () => supportThreads.filter((t) => t.last_agent_seq > getSupportSeenSeq(t.conversation_id)),
    [supportThreads, seenTick],
  );

  const openPanel = () => {
    const target = unreadSupportThreads[0];
    if (target) {
      if (target.conversation_id !== chat.conversationId) void chat.loadConversation(target.conversation_id);
      nav.openChat();
    }
    setOpen(true);
  };

  useEffect(() => {
    if (!user) return;
    let alive = true;
    getAssistantStatus()
      .then((s) => alive && setStatus(s))
      .catch(() => alive && setStatus(null));
    return () => {
      alive = false;
    };
  }, [user, user?.current_unit?.id]);

  useEffect(() => {
    const onAsk = (ev: Event) => {
      const message = (ev as CustomEvent<{ message: string }>).detail?.message;
      if (!message) return;
      setOpen(true);
      openChatRef.current();
      void chatRef.current.send(message);
    };
    const onError = (ev: Event) => {
      const detail = (ev as CustomEvent<AssistantRecentError>).detail;
      if (!detail || detail.status === 401 || !detail.message) return;
      setLastError(detail);
      if (errorTimer.current) clearTimeout(errorTimer.current);
      errorTimer.current = setTimeout(() => setLastError(null), ERROR_PROMPT_MS);
    };
    window.addEventListener(ASSISTANT_ASK_EVENT, onAsk);
    window.addEventListener(ASSISTANT_ERROR_EVENT, onError);
    return () => {
      window.removeEventListener(ASSISTANT_ASK_EVENT, onAsk);
      window.removeEventListener(ASSISTANT_ERROR_EVENT, onError);
      if (errorTimer.current) clearTimeout(errorTimer.current);
    };
  }, []);

  if (!user) return null;
  if (status && !status.enabled) return null;

  /** Rotas com composer fixo e a própria página da central — sem FAB. */
  const hideOnRoute =
    /^\/inbox(\/|$)/.test(location.pathname) ||
    /^\/team-chat(\/|$)/.test(location.pathname) ||
    /^\/central-ia(\/|$)/.test(location.pathname);
  if (hideOnRoute) return null;

  /** Mobile: sem FAB lateral — use o menu (/central-ia). */
  if (mobileEdgeDock) return null;

  const fabRight = `calc(env(safe-area-inset-right, 0px) + ${DOCK_CSS.assistantFabRight})`;
  const fabBottom = `calc(env(safe-area-inset-bottom, 0px) + ${DOCK_CSS.pillBottom})`;
  const panelBottom = `calc(env(safe-area-inset-bottom, 0px) + ${DOCK_CSS.bottomClearance})`;
  const errorChipRight = `calc(${DOCK_CSS.teamChatCardsRight} + 16px)`;

  const askAboutError = () => {
    if (!lastError) return;
    setLastError(null);
    setOpen(true);
    nav.openChat();
    void chat.send(`Apareceu este erro: "${lastError.message}". O que significa e como resolvo?`);
  };

  return (
    <>
      {lastError && !open && (
        <Zoom in>
          <Chip
            icon={<HelpOutlineIcon sx={{ fontSize: 16 }} />}
            label="Perguntar à IA sobre este erro"
            onClick={askAboutError}
            onDelete={() => setLastError(null)}
            color="primary"
            sx={{
              position: 'fixed',
              zIndex: (theme) => theme.zIndex.modal + 2,
              boxShadow: 3,
              fontSize: '0.75rem',
              ...(mobileEdgeDock
                ? {
                    left: 12,
                    right: 'auto',
                    bottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
                    maxWidth: 'calc(100vw - 56px)',
                  }
                : {
                    right: `calc(env(safe-area-inset-right, 0px) + ${errorChipRight})`,
                    bottom: 'calc(env(safe-area-inset-bottom, 0px) + 28px)',
                  }),
            }}
          />
        </Zoom>
      )}

      <Zoom in>
        <Tooltip title={`${SUPPORT_CENTER_DISPLAY_NAME} — ${getPlatformName()}`} placement="left">
          <Badge
            color="error"
            badgeContent={unreadSupportThreads.length}
            invisible={open || !unreadSupportThreads.length}
            anchorOrigin={mobileEdgeDock ? { vertical: 'top', horizontal: 'left' } : undefined}
            sx={{
              position: 'fixed',
              zIndex: (theme) => theme.zIndex.modal + 2,
              ...(mobileEdgeDock
                ? { ...mobileEdgeTabFixedSx, bottom: mobileEdgeTabBottom('support') }
                : { right: fabRight, bottom: fabBottom }),
            }}
          >
            <ButtonBase
              aria-label={
                open
                  ? `Fechar ${SUPPORT_CENTER_DISPLAY_NAME}`
                  : `Abrir ${SUPPORT_CENTER_DISPLAY_NAME} — ${getPlatformName()}`
              }
              aria-expanded={open}
              onClick={() => (open ? setOpen(false) : openPanel())}
              sx={{
                ...(mobileEdgeDock ? assistantMobileEdgeTabSx : assistantFabSx),
                position: 'relative',
              }}
            >
              {mobileEdgeDock ? (
                open ? (
                  <>
                    <CloseIcon sx={{ color: '#fff', fontSize: 17 }} aria-hidden />
                    <Box component="span" sx={mobileEdgeTabLabelSx}>Fechar</Box>
                  </>
                ) : (
                  <>
                    <Box
                      component="span"
                      sx={{ ...assistantPillDotSx, width: 6, height: 6, boxShadow: '0 0 0 2px rgba(167, 243, 208, 0.35)' }}
                      aria-hidden
                    />
                    <Box component="span" sx={mobileEdgeTabLabelSx}>Suporte</Box>
                  </>
                )
              ) : open ? (
                <>
                  <CloseIcon sx={{ color: '#fff', fontSize: 18 }} aria-hidden />
                  <span>Fechar</span>
                </>
              ) : (
                <>
                  <Box component="span" sx={assistantPillDotSx} aria-hidden />
                  <span>Suporte</span>
                </>
              )}
            </ButtonBase>
          </Badge>
        </Tooltip>
      </Zoom>

      {open && (
        <Paper
          elevation={0}
          sx={{
            ...assistantPanelPaperSx,
            display: 'flex',
            flexDirection: 'column',
            zIndex: (theme) => theme.zIndex.modal + 1,
            ...(mobileEdgeDock
              ? {
                  left: 0,
                  right: 0,
                  bottom: 0,
                  width: '100%',
                  maxWidth: '100%',
                  height: 'min(92dvh, 780px)',
                  borderRadius: '20px 20px 0 0',
                }
              : {
                  right: `calc(env(safe-area-inset-right, 0px) + ${DOCK_CSS.teamFabRight})`,
                  bottom: panelBottom,
                  ...assistantWidgetPanelSizeSx,
                }),
          }}
        >
          <SupportMessenger
            nav={nav}
            chat={chat}
            open={open}
            brandName={getPlatformName()}
            firstName={status?.session?.user_name?.trim().split(/\s+/)[0]}
            unreadConversationIds={unreadSupportThreads.map((t) => t.conversation_id)}
            chatNotice={
              <>
                <AssistantSessionContextNotice session={status?.session} />
                <AssistantStatusNotice
                  status={status}
                  onOpenSettings={() => {
                    setOpen(false);
                    navigate('/central-ia?tab=settings');
                  }}
                />
              </>
            }
            onClose={() => setOpen(false)}
            onNavigate={(href) => {
              if (/^https?:\/\//i.test(href)) {
                window.open(href, '_blank', 'noopener,noreferrer');
                return;
              }
              setOpen(false);
              navigate(href);
            }}
            onOpenFullScreen={() => {
              setOpen(false);
              navigate(chat.conversationId ? `/central-ia?c=${encodeURIComponent(chat.conversationId)}` : '/central-ia');
            }}
          />
        </Paper>
      )}
    </>
  );
};

export default VirtualAssistantWidget;
